import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { useRef } from 'react';

import type { Ref } from 'react';

const OVERLAY_CONTENT = '[role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper]';

// A press on a toast, or on an overlay that opened after `content` and so paints above it, must not dismiss `content`.
// sonner renders toasts in place, so they count as above whatever their position in the DOM.
function preventDismissFromAbove(event: Event, content: Element | null) {
  if (!(event.target instanceof Element)) return;

  const overlay = event.target.closest(OVERLAY_CONTENT);
  const isOverlayAbove = Boolean(
    overlay && content && content.compareDocumentPosition(overlay) & Node.DOCUMENT_POSITION_FOLLOWING,
  );
  if (isOverlayAbove || event.target.closest('[data-sonner-toaster]')) {
    event.preventDefault();
  }
}

type DismissGuard = <E extends Event>(handler?: (event: E) => void) => (event: E) => void;

export function useDismissFromAbove<T extends Element>(forwardedRef: Ref<T>): [(node: T) => void, DismissGuard] {
  const contentRef = useRef<T>(null);
  const setContent = useComposedRefs(forwardedRef, contentRef);
  const guard: DismissGuard = handler => event => {
    preventDismissFromAbove(event, contentRef.current);
    handler?.(event);
  };
  return [setContent, guard];
}
